import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, MapPin, Phone, Mail, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * /showroom — By-appointment booking page.
 *
 * Users pick a preferred date + time slot, add a note, and submit via
 * `mailto:` so the request lands in the sales inbox without needing an
 * always-on backend send. Direct call + email actions sit alongside the form
 * for people who'd rather not use it. Available slots reflect the current
 * showroom availability policy (Mon–Fri hourly, limited Sat).
 */

const ease = [0.19, 1, 0.22, 1];

const LOUNGE_BG =
  "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/567lqmtp_DSCF6580.webp";
const ARCH_DOOR =
  "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/tx7gkktb_670D9C1A-8086-400A-B41C-FB27AC598E65.JPG";

const WEEKDAY_SLOTS = ["8:30 AM", "9:30 AM", "10:30 AM", "11:30 AM", "12:30 PM", "1:30 PM", "2:30 PM", "3:30 PM", "4:30 PM"];
const SATURDAY_SLOTS = WEEKDAY_SLOTS;

const HOURS = [
  { day: "Mon – Fri", time: "8:30 AM – 5:00 PM" },
  { day: "Showroom", time: "By appointment" },
  { day: "Sunday", time: "Closed" },
];

// Compute tomorrow's ISO date to seed the calendar input (avoid same-day noise).
function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function slotsForDate(iso) {
  if (!iso) return WEEKDAY_SLOTS;
  const dow = new Date(iso + "T00:00:00").getDay(); // 0=Sun … 6=Sat
  if (dow === 0) return [];
  if (dow === 6) return SATURDAY_SLOTS;
  return WEEKDAY_SLOTS;
}

function humanDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function Showroom() {
  const [date, setDate] = useState(tomorrowISO());
  const [slot, setSlot] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const slots = useMemo(() => slotsForDate(date), [date]);
  const closed = slots.length === 0;

  const canSubmit = Boolean(date) && !closed && Boolean(slot) && name.trim() && email.trim();

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    setError(null);
    setSending(true);
    try {
      // Route showroom booking through /api/quotes → Resend → orders@euro-steel.co.nz.
      // Date + slot + name arrive as a neat spec_summary table; "what to see" is Notes.
      await axios.post(`${BACKEND}/api/quotes`, {
        product_code: "SHOWROOM",
        product_name: `Showroom Booking — ${humanDate(date)} · ${slot}`,
        email: email.trim(),
        phone: phone.trim() || null,
        notes: notes.trim() || null,
        spec_summary: [
          { label: "Full Name", value: name.trim() },
          { label: "Preferred Date", value: humanDate(date) },
          { label: "Preferred Time", value: slot },
          ...(phone.trim()
            ? [{ label: "Phone", value: phone.trim() }]
            : []),
        ],
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Sorry — we couldn't send your booking right now. Please try again or email orders@euro-steel.co.nz directly.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="showroom-page" className="relative min-h-screen bg-white text-[#0a0a0c]">
      {/* Hero */}
      <section className="relative mx-auto max-w-[1400px] px-6 pb-14 pt-40 lg:px-16 lg:pt-48">
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          data-testid="showroom-heading"
          className="mt-4 font-serif-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-7xl"
        >
          Book a Showroom Visit.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease }}
          data-testid="showroom-eyebrow"
          className="mt-6 font-tech text-[11px] font-semibold uppercase tracking-[0.42em] text-[#c6a87c]"
        >
          By Appointment · Auckland
        </motion.p>
      </section>

      {/* Body */}
      <section className="relative mx-auto max-w-[1400px] border-t border-[#0a0a0c]/10 px-6 pb-24 lg:px-16 lg:pb-32">
        {/* Ambient lounge photograph — visible backdrop behind the booking form */}
        <div
          aria-hidden
          data-testid="showroom-bg"
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat opacity-70"
          style={{ backgroundImage: `url(${LOUNGE_BG})` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/55 via-white/40 to-white/70"
        />
        <div className="relative">
        <div className="grid grid-cols-1 gap-14 pt-12 lg:grid-cols-[1fr_360px] lg:gap-20 lg:pt-16">
          {/* Booking form */}
          <div className="rounded-md bg-white/85 p-6 backdrop-blur-md ring-1 ring-black/5 shadow-[0_10px_40px_-20px_rgba(10,10,12,0.25)] sm:p-8 lg:p-10">
            <h2 className="flex items-center gap-3 font-serif-display text-3xl font-black uppercase tracking-tight text-[#0a0a0c] lg:text-4xl">
              <Calendar size={22} strokeWidth={2} className="text-[#c6a87c]" />
              Pick a time.
            </h2>
            <p className="mt-3 max-w-xl font-body text-[14px] font-semibold text-[#0a0a0c]/85">
              Choose a preferred date and slot below. We'll confirm by email within one
              business day. Our showroom is at <span className="whitespace-nowrap text-[#0a0a0c]">3 Byron Avenue, Takapuna</span>.
            </p>

            <div className="mt-10 space-y-8">
              {/* Date */}
              <div>
                <label
                  htmlFor="visit-date"
                  className="mb-2 block font-tech text-[10px] font-black uppercase tracking-[0.3em] text-[#0a0a0c]"
                >
                  Preferred date
                </label>
                <input
                  id="visit-date"
                  type="date"
                  data-testid="showroom-date"
                  value={date}
                  min={tomorrowISO()}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setSlot("");
                  }}
                  className="w-full max-w-[320px] border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[15px] text-[#0a0a0c] transition-colors focus:border-[#c6a87c] focus:outline-none"
                />
                {date && (
                  <p className="mt-2 font-tech text-[11px] uppercase tracking-[0.24em] text-[#0a0a0c]/55">
                    {humanDate(date)}
                  </p>
                )}
              </div>

              {/* Slots */}
              <div>
                  <label className="mb-3 block font-tech text-[10px] font-black uppercase tracking-[0.3em] text-[#0a0a0c]">
                  Available times
                </label>
                {closed ? (
                  <p
                    data-testid="showroom-closed"
                    className="rounded-md border border-dashed border-[#0a0a0c]/20 bg-[#f6f4ef] p-4 font-body text-[14px] text-[#0a0a0c]/65"
                  >
                    Sunday is closed. Please choose another day.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {slots.map((s) => {
                      const isActive = slot === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          data-testid={`showroom-slot-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => setSlot(s)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 font-tech text-[11px] font-semibold uppercase tracking-[0.24em] transition-all duration-300 ${
                            isActive
                              ? "border-[#c6a87c] bg-[#c6a87c] text-[#0a0a0c] shadow-[0_0_0_3px_rgba(198,168,124,0.2)]"
                              : "border-[#0a0a0c]/15 text-[#0a0a0c] hover:border-[#c6a87c] hover:text-[#c6a87c]"
                          }`}
                        >
                          <Clock size={12} strokeWidth={1.75} />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Contact fields */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="visit-name" className="mb-2 block font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                    Full name
                  </label>
                  <input
                    id="visit-name"
                    type="text"
                    data-testid="showroom-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[15px] text-[#0a0a0c] transition-colors focus:border-[#c6a87c] focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="visit-email" className="mb-2 block font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                    Email
                  </label>
                  <input
                    id="visit-email"
                    type="email"
                    data-testid="showroom-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[15px] text-[#0a0a0c] transition-colors focus:border-[#c6a87c] focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="visit-phone" className="mb-2 block font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                    Phone (optional)
                  </label>
                  <input
                    id="visit-phone"
                    type="tel"
                    data-testid="showroom-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+64 …"
                    className="w-full border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[15px] text-[#0a0a0c] transition-colors focus:border-[#c6a87c] focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="visit-notes" className="mb-2 block font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                    What would you like to see?
                  </label>
                  <textarea
                    id="visit-notes"
                    data-testid="showroom-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Which profiles / products, project details, anything specific to prepare…"
                    className="w-full resize-none border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[15px] text-[#0a0a0c] transition-colors focus:border-[#c6a87c] focus:outline-none"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-body text-[13px] font-semibold text-[#0a0a0c]/80">
                  {sent
                    ? "Your booking request has been sent — we'll confirm by email within one business day."
                    : "We'll confirm your slot by email within one business day."}
                </p>
                <button
                  type="button"
                  onClick={submitBooking}
                  disabled={!canSubmit || sending || sent}
                  data-testid="showroom-submit"
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-tech text-[11px] font-bold uppercase tracking-[0.28em] transition-all duration-500 ${
                    sent
                      ? "bg-[#c6a87c] text-[#0a0a0c]"
                      : canSubmit && !sending
                        ? "bg-[#0a0a0c] text-white hover:bg-[#c6a87c] hover:text-[#0a0a0c]"
                        : "cursor-not-allowed bg-[#0a0a0c]/25 text-white/70"
                  }`}
                >
                  {sent
                    ? "Booking Sent ✓"
                    : sending
                      ? "Sending…"
                      : "Request Booking"}
                  {!sent && <ArrowRight size={14} strokeWidth={2} />}
                </button>
              </div>

              {error && (
                <p
                  data-testid="showroom-error"
                  className="font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-red-600"
                >
                  {error}
                </p>
              )}
            </div>
          </div>

          {/* Sidebar — hours + direct contact */}
          <aside className="lg:sticky lg:top-40 lg:h-fit">
            <div className="border border-[#0a0a0c]/10 bg-[#faf8f2] p-8">
              <p className="flex items-center gap-2 font-tech text-[10px] font-bold uppercase tracking-[0.32em] text-[#c6a87c]">
                <MapPin size={14} strokeWidth={1.75} /> Auckland
              </p>
              <h3 className="mt-3 font-serif-display text-2xl font-bold uppercase tracking-tight text-[#0a0a0c]">
                Flagship Showroom
              </h3>
              <a
                href="https://maps.google.com/?q=3+Byron+Avenue+Takapuna+Auckland"
                target="_blank"
                rel="noreferrer"
                data-testid="showroom-address"
                className="group mt-4 flex items-start gap-2 font-body text-[14px] leading-[1.6] text-[#0a0a0c] transition-colors hover:text-[#c6a87c]"
              >
                <MapPin size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-[#c6a87c]" />
                <span>
                  <span className="font-semibold">3 Byron Avenue</span>
                  <br />
                  Takapuna, Auckland
                </span>
              </a>
              <p className="mt-4 font-body text-[13px] leading-[1.7] text-[#0a0a0c]/65">
                Appointments only, so we can dedicate the space to you.
              </p>

              <div className="mt-8 space-y-3 border-t border-[#0a0a0c]/10 pt-6">
                <p className="font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                  Opening hours
                </p>
                <ul className="space-y-1.5">
                  {HOURS.map((h) => (
                    <li key={h.day} className="flex items-baseline justify-between gap-4 font-body text-[13px]">
                      <span className="font-semibold text-[#0a0a0c]">{h.day}</span>
                      <span className="text-right text-[#0a0a0c]/70">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 space-y-3 border-t border-[#0a0a0c]/10 pt-6">
                <p className="font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/60">
                  Prefer to talk?
                </p>
                <a
                  href="tel:+64279152526"
                  data-testid="showroom-direct-call"
                  className="group flex items-center gap-3 py-1 font-body text-[15px] font-semibold text-[#0a0a0c] transition-colors hover:text-[#c6a87c]"
                >
                  <Phone size={15} strokeWidth={1.75} className="text-[#c6a87c]" />
                  +64 27 915 2526
                </a>
                <a
                  href="mailto:orders@euro-steel.co.nz?subject=Showroom%20Appointment%20Booking"
                  data-testid="showroom-direct-email"
                  className="group flex items-center gap-3 py-1 font-body text-[15px] font-semibold text-[#0a0a0c] transition-colors hover:text-[#c6a87c]"
                >
                  <Mail size={15} strokeWidth={1.75} className="text-[#c6a87c]" />
                  orders@euro-steel.co.nz
                </a>
              </div>

              <Link
                to="/contact"
                data-testid="showroom-contact-link"
                className="mt-8 inline-flex w-full items-center justify-center rounded-full border border-[#0a0a0c]/15 px-6 py-3 font-tech text-[10px] font-semibold uppercase tracking-[0.28em] text-[#0a0a0c] transition-all duration-300 hover:border-[#c6a87c] hover:text-[#c6a87c]"
              >
                General Contact Options
              </Link>
            </div>
          </aside>
        </div>
        </div>
      </section>

      {/* Feature image — arched door display (below booking) */}
      <section
        data-testid="showroom-gallery"
        aria-label="Inside the Auckland showroom"
        className="relative mx-auto max-w-[1400px] px-6 pb-20 lg:px-16 lg:pb-28"
      >
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 0.9, ease }}
          className="mx-auto max-w-[560px]"
        >
          <img
            src={ARCH_DOOR}
            alt=""
            loading="lazy"
            className="block h-auto w-full"
          />
        </motion.figure>
      </section>
    </div>
  );
}
